import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebaseConfig';
import { collectionGroup, query, getDocs, doc, deleteDoc, getDoc } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import RoomEditModal from './RoomEditModal';

function RoomManagement() {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingRoomId, setDeletingRoomId] = useState(null);
  const [editingRoom, setEditingRoom] = useState(null);

  const fetchRooms = async () => {
    setLoading(true);
    try {
      const roomsCollectionGroupRef = collectionGroup(db, 'rooms');
      const querySnapshot = await getDocs(query(roomsCollectionGroupRef));

      const fetchedRooms = [];
      const uniqueOwnerIds = new Set();

      querySnapshot.docs.forEach(doc => {
        const roomData = { id: doc.id, ownerId: doc.ref.parent.parent.id, ...doc.data() };
        fetchedRooms.push(roomData);
        uniqueOwnerIds.add(roomData.ownerId);
      });

      const ownerNames = new Map();
      const ownerPromises = Array.from(uniqueOwnerIds).map(async (ownerId) => {
        const userDocSnap = await getDoc(doc(db, 'users', ownerId));
        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          ownerNames.set(ownerId, { firstName: userData.firstName || 'Unknown', lastName: userData.lastName || '' });
        } else {
          ownerNames.set(ownerId, { firstName: 'Deleted', lastName: 'User' });
        }
      });

      await Promise.all(ownerPromises);
      const roomsWithOwners = fetchedRooms.map(room => ({
        ...room,
        ownerFullName: `${ownerNames.get(room.ownerId)?.firstName || 'Unknown'} ${ownerNames.get(room.ownerId)?.lastName || ''}`.trim(),
      }));

      setRooms(roomsWithOwners);
    } catch (err) {
      console.error(err);
      setError('Failed to load rooms.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRoom = async (roomId, ownerId, imageUrls) => {
    if (!window.confirm('Delete this room?')) return;
    setDeletingRoomId(roomId);
    try {
      if (imageUrls?.length > 0) {
        await Promise.all(imageUrls.map(async (url) => {
          try {
            const path = decodeURIComponent(url.split('/o/')[1].split('?')[0]);
            await deleteObject(ref(storage, path));
          } catch (e) { console.warn('Image delete error', e); }
        }));
      }
      await deleteDoc(doc(db, 'roomsData', ownerId, 'rooms', roomId));
      setRooms(prev => prev.filter(r => r.id !== roomId));
    } catch (err) {
      setError('Failed to delete room.');
    } finally {
      setDeletingRoomId(null);
    }
  };

  useEffect(() => { fetchRooms(); }, []);

  if (loading) return <div style={styles.loading}>Loading...</div>;

  return (
    <div style={styles.container}>
      <style>{`
        @media (max-width: 768px) {
          .desktop-table { display: none !important; }
          .mobile-card { display: block !important; }
        }
        @media (min-width: 769px) {
          .mobile-card { display: none !important; }
        }
      `}</style>

      <h2 style={styles.title}>Room Listings ({rooms.length})</h2>

      {/* DESKTOP TABLE VIEW */}
      <table style={styles.table} className="desktop-table">
        <thead>
          <tr style={styles.tr}>
            <th style={styles.th}>Title</th>
            <th style={styles.th}>Uploader</th>
            <th style={styles.th}>Price</th>
            <th style={styles.th}>Status</th>
            <th style={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rooms.map(room => (
            <tr key={room.id} style={styles.tr}>
              <td style={styles.td}>{room.title}</td>
              <td style={styles.td}>{room.ownerFullName}</td>
              <td style={styles.td}>Nrs. {room.price}</td>
              <td style={styles.td}>
                <span style={room.status === 'active' ? styles.activeBadge : styles.inactiveBadge}>
                  {room.status ? room.status.toUpperCase() : 'N/A'}
                </span>
              </td>
              <td style={styles.tdActions}>
                <button onClick={() => setEditingRoom(room)} style={styles.editBtn}>Edit</button>
                <button onClick={() => handleDeleteRoom(room.id, room.ownerId, room.imageUrls)} style={styles.delBtn}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* MOBILE CARD VIEW */}
      <div className="mobile-card">
        {rooms.map(room => (
          <div key={room.id} style={styles.card}>
            <div style={styles.cardHeader}><strong>{room.title}</strong></div>
            <div style={styles.cardBody}>
              <p><strong>Uploader:</strong> {room.ownerFullName}</p>
              <p><strong>Price:</strong> Nrs. {room.price}</p>
              <p>
                <strong>Status:</strong> 
                <span style={room.status === 'active' ? styles.activeBadge : styles.inactiveBadge}>
                  {room.status ? room.status.toUpperCase() : 'N/A'}
                </span>
              </p>
            </div>
            <div style={styles.cardActions}>
              <button onClick={() => setEditingRoom(room)} style={styles.editBtn}>Edit</button>
              <button onClick={() => handleDeleteRoom(room.id, room.ownerId, room.imageUrls)} style={styles.delBtn}>Delete</button>
            </div>
          </div>
        ))}
      </div>

      {editingRoom && (
        <RoomEditModal room={editingRoom} onClose={() => setEditingRoom(null)} onSaveSuccess={fetchRooms} />
      )}
    </div>
  );
}

const styles = {
  container: { padding: '20px', backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' },
  title: { fontSize: '20px', marginBottom: '20px', color: '#1e293b' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '12px', color: '#64748b', fontSize: '14px', borderBottom: '2px solid #f1f5f9' },
  td: { padding: '12px', fontSize: '14px', borderBottom: '1px solid #f1f5f9', color: '#334155' },
  tdActions: { padding: '12px', borderBottom: '1px solid #f1f5f9' },
  // Pill Badge Styles
  activeBadge: { backgroundColor: '#dcfce7', color: '#166534', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold' },
  inactiveBadge: { backgroundColor: '#fee2e2', color: '#991b1b', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold' },
  editBtn: { padding: '6px 12px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', marginRight: '5px' },
  delBtn: { padding: '6px 12px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' },
  card: { border: '1px solid #e2e8f0', borderRadius: '10px', padding: '15px', marginBottom: '15px', backgroundColor: '#f8fafc' },
  cardHeader: { marginBottom: '10px', fontSize: '16px', color: '#1e293b' },
  cardBody: { fontSize: '14px', color: '#475569', marginBottom: '15px' },
  cardActions: { display: 'flex', gap: '10px' },
  loading: { textAlign: 'center', padding: '40px' }
};

export default RoomManagement;